import { Card, CircularProgress, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { UserContext } from "../../../context/UserContext";
import { isChmCourse } from "../../../Helpers";
import { CourseStructureCtx, UserCtx } from "../../../interfaces";
import axios from "../../../services/axios";
import { logger } from "../../../services/logger";

interface CourseProgressData {
  points: number;
  max_points: number;
}

export const CourseProgressCard = () => {
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const userCtx = useContext(UserContext) as UserCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const studentClass = userCtx.studentCurrentClass;
  const [courseProgress, setCourseProgress] = useState<CourseProgressData | null>(null);
  const [loading, setLoading] = useState(false);

  const isChm = useMemo(
    () =>
      isChmCourse({
        courseName: studentClass?.course?.name,
        classSubject: studentClass?.subject,
        faqCourseTag: courseMetadata?.faq_course_tag,
        progressDisplayType: courseMetadata?.module_overview_page?.progress_display_type,
      }),
    [
      studentClass?.course?.name,
      studentClass?.subject,
      courseMetadata?.faq_course_tag,
      courseMetadata?.module_overview_page?.progress_display_type,
    ]
  );

  useEffect(() => {
    if (!isChm || !studentClass?.id) {
      setCourseProgress(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    axios
      .get<CourseProgressData>("/course-learning-map-progress")
      .then((response) => {
        if (cancelled) {
          return;
        }

        const { points, max_points } = response.data;
        if (typeof points === "number" && typeof max_points === "number") {
          setCourseProgress({ points, max_points });
        }
      })
      .catch((err) => {
        logger.error(err);
        if (!cancelled) {
          setCourseProgress(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isChm, studentClass?.id, userCtx.userQuizSubmissions?.length]);

  if (!isChm) {
    return null;
  }

  const progressLabel = (() => {
    if (loading) {
      return <CircularProgress size={24} sx={{ marginTop: "8px" }} />;
    }

    if (!courseProgress) {
      return null;
    }

    return (
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: "600 !important",
          lineHeight: "32px",
          color: "#191919",
        }}
        variant="h4">
        {courseProgress.points}/{courseProgress.max_points}
      </Typography>
    );
  })();

  if (!progressLabel) {
    return null;
  }

  return (
    <Card
      sx={{
        marginBottom: "24px",
        padding: "32px",
        borderRadius: "16px",
        border: "2px solid #DEDEDE",
        background: "#FFF",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "24px",
      }}>
      <Typography
        variant="h5"
        component="span"
        sx={{
          fontSize: "28px",
          fontWeight: "700 !important",
          color: "#191919",
          letterSpacing: "0.15px",
        }}>
        Questions Attempted
      </Typography>
      {progressLabel}
    </Card>
  );
};
